/**
 * Hook to compute story-specific data insights from FAAC data
 */

import { useMemo } from 'react';
import type { ProcessedData } from '../types';
import { 
  OIL_STATES, 
  NORTHERN_STATES, 
  SOUTHERN_STATES 
} from '../utils/statePathGenerator';

export interface StoryInsights {
  // Scene 1: Total overview
  totalAllocation: number;
  totalAllocationFormatted: string;
  yearsSpan: { start: number; end: number };
  totalStates: number;
  
  // Scene 2: Oil giants
  oilStatesTotal: number;
  oilStatesPercentage: number;
  topOilStates: { state: string; total: number; percentage: number }[];
  nonOilStatesTotal: number;
  
  // Scene 3: Timeline data
  yearlyTotals: { year: number; total: number }[];
  yearOverYearChanges: { year: number; change: number; percentChange: number }[];
  peakYear: { year: number; total: number };
  lowYear: { year: number; total: number };
  
  // Scene 4: North vs South
  northTotal: number;
  southTotal: number;
  northStatesCount: number;
  southStatesCount: number;
  northAverage: number;
  southAverage: number;
  
  // Scene 5: Rankings over time
  rankingHistory: { year: number; rankings: { state: string; total: number; rank: number }[] }[];
  consistentLeaders: string[];
  
  // Scene 6: Growth champions
  growthRankings: { state: string; percentGrowth: number; startTotal: number; endTotal: number }[];
  topGrowthStates: { state: string; percentGrowth: number }[];
  bottomGrowthStates: { state: string; percentGrowth: number }[];
  
  // Scene 7: Volatility
  volatilityRankings: { state: string; volatility: number; isOilState: boolean }[];
  mostVolatile: { state: string; volatility: number }[];
  mostStable: { state: string; volatility: number }[];
}

function formatLargeNumber(num: number): string {
  if (num >= 1e12) {
    return `₦${(num / 1e12).toFixed(2)} Trillion`;
  }
  if (num >= 1e9) {
    return `₦${(num / 1e9).toFixed(2)} Billion`;
  }
  if (num >= 1e6) {
    return `₦${(num / 1e6).toFixed(2)} Million`;
  }
  return `₦${num.toLocaleString()}`;
}

function calculateVolatility(yearlyTotals: number[]): number {
  if (yearlyTotals.length < 2) return 0;
  
  const mean = yearlyTotals.reduce((a, b) => a + b, 0) / yearlyTotals.length;
  const squaredDiffs = yearlyTotals.map(val => Math.pow(val - mean, 2));
  const avgSquaredDiff = squaredDiffs.reduce((a, b) => a + b, 0) / squaredDiffs.length;
  const stdDev = Math.sqrt(avgSquaredDiff);
  
  // Coefficient of variation (normalized volatility)
  return (stdDev / mean) * 100;
}

export function useStoryData(data: ProcessedData | null): StoryInsights | null {
  return useMemo(() => {
    if (!data) return null;
    
    const { stateSummaries, yearSummaries, years, states } = data;
    
    // Scene 1: Calculate total allocation
    let totalAllocation = 0;
    for (const summary of stateSummaries.values()) {
      totalAllocation += summary.allTimeTotal;
    }
    
    // Scene 2: Oil states analysis
    const oilStatesData: { state: string; total: number }[] = [];
    let oilStatesTotal = 0;
    
    for (const [stateName, summary] of stateSummaries) {
      if (OIL_STATES.includes(stateName)) {
        oilStatesData.push({ state: stateName, total: summary.allTimeTotal });
        oilStatesTotal += summary.allTimeTotal;
      }
    }
    
    oilStatesData.sort((a, b) => b.total - a.total);
    const topOilStates = oilStatesData.slice(0, 4).map(s => ({
      ...s,
      percentage: (s.total / totalAllocation) * 100,
    }));
    
    // Scene 3: Timeline data
    const yearlyTotals: { year: number; total: number }[] = [];
    for (const [year, summary] of yearSummaries) {
      yearlyTotals.push({ year, total: summary.totalAllocation });
    }
    yearlyTotals.sort((a, b) => a.year - b.year);
    
    const yearOverYearChanges = yearlyTotals.slice(1).map((curr, i) => {
      const prev = yearlyTotals[i];
      const change = curr.total - prev.total;
      const percentChange = (change / prev.total) * 100;
      return { year: curr.year, change, percentChange };
    });
    
    const peakYear = yearlyTotals.reduce((max, curr) => 
      curr.total > max.total ? curr : max, yearlyTotals[0]);
    const lowYear = yearlyTotals.reduce((min, curr) => 
      curr.total < min.total ? curr : min, yearlyTotals[0]);
    
    // Scene 4: North vs South
    let northTotal = 0;
    let southTotal = 0;
    let northCount = 0;
    let southCount = 0;
    
    for (const [stateName, summary] of stateSummaries) {
      if (NORTHERN_STATES.includes(stateName)) {
        northTotal += summary.allTimeTotal;
        northCount++;
      } else if (SOUTHERN_STATES.includes(stateName)) {
        southTotal += summary.allTimeTotal;
        southCount++;
      }
    }
    
    // Scene 5: Rankings history
    const rankingHistory = years.map(year => {
      const summary = yearSummaries.get(year);
      if (!summary) return { year, rankings: [] };
      
      return {
        year,
        rankings: summary.rankings.slice(0, 10).map(r => ({
          state: r.state,
          total: r.total,
          rank: r.rank,
        })),
      };
    });
    
    // Find consistent top 5 leaders
    const leaderCounts = new Map<string, number>();
    for (const { rankings } of rankingHistory) {
      rankings.slice(0, 5).forEach(r => {
        leaderCounts.set(r.state, (leaderCounts.get(r.state) || 0) + 1);
      });
    }
    const consistentLeaders = Array.from(leaderCounts.entries())
      .filter(([, count]) => count >= years.length * 0.8)
      .map(([state]) => state);
    
    // Scene 6: Growth analysis
    const growthRankings: { state: string; percentGrowth: number; startTotal: number; endTotal: number }[] = [];
    
    const startYear = years[0];
    const endYear = years[years.length - 1];
    
    for (const [stateName, summary] of stateSummaries) {
      const startData = summary.yearlyData.get(startYear);
      const endData = summary.yearlyData.get(endYear);
      
      if (startData && endData && startData.total > 0) {
        const percentGrowth = ((endData.total - startData.total) / startData.total) * 100;
        growthRankings.push({
          state: stateName,
          percentGrowth,
          startTotal: startData.total,
          endTotal: endData.total,
        });
      }
    }
    
    growthRankings.sort((a, b) => b.percentGrowth - a.percentGrowth);
    
    // Scene 7: Volatility analysis
    const volatilityRankings: { state: string; volatility: number; isOilState: boolean }[] = [];
    
    for (const [stateName, summary] of stateSummaries) {
      const yearlyValues = Array.from(summary.yearlyData.values()).map(d => d.total);
      const volatility = calculateVolatility(yearlyValues);
      
      volatilityRankings.push({
        state: stateName,
        volatility,
        isOilState: OIL_STATES.includes(stateName),
      });
    }
    
    volatilityRankings.sort((a, b) => b.volatility - a.volatility);
    
    return {
      // Scene 1
      totalAllocation,
      totalAllocationFormatted: formatLargeNumber(totalAllocation),
      yearsSpan: { start: years[0], end: years[years.length - 1] },
      totalStates: states.length,
      
      // Scene 2
      oilStatesTotal,
      oilStatesPercentage: (oilStatesTotal / totalAllocation) * 100,
      topOilStates,
      nonOilStatesTotal: totalAllocation - oilStatesTotal,
      
      // Scene 3
      yearlyTotals,
      yearOverYearChanges,
      peakYear,
      lowYear,
      
      // Scene 4
      northTotal,
      southTotal,
      northStatesCount: northCount,
      southStatesCount: southCount,
      northAverage: northCount > 0 ? northTotal / northCount : 0,
      southAverage: southCount > 0 ? southTotal / southCount : 0,
      
      // Scene 5
      rankingHistory,
      consistentLeaders,
      
      // Scene 6
      growthRankings,
      topGrowthStates: growthRankings.slice(0, 5).map(s => ({ 
        state: s.state, 
        percentGrowth: s.percentGrowth 
      })),
      bottomGrowthStates: growthRankings.slice(-5).reverse().map(s => ({ 
        state: s.state, 
        percentGrowth: s.percentGrowth 
      })),
      
      // Scene 7
      volatilityRankings,
      mostVolatile: volatilityRankings.slice(0, 5).map(s => ({ 
        state: s.state, 
        volatility: s.volatility 
      })),
      mostStable: volatilityRankings.slice(-5).reverse().map(s => ({ 
        state: s.state, 
        volatility: s.volatility 
      })),
    };
  }, [data]);
}

export default useStoryData;

